import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Card, Empty, Loading } from "@/components/Shared";
import { formatINR } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Truck, Loader2, Search, X, ChevronRight } from "lucide-react";

const STATUS = { Draft: "bg-[#EEF0F5] text-[#6B7189]", Sent: "bg-[#EAF3FF] text-[#1D4ED8]", "Partially Received": "bg-[#FEF3C7] text-[#B45309]", Received: "bg-[#DCFCE7] text-[#15803D]", Cancelled: "bg-[#FEE2E2] text-[#B91C1C]" };
const fdate = (s) => (s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" }) : "—");
const Badge = ({ s }) => <span className={`text-[10px] rounded-full px-2 py-0.5 ${STATUS[s] || "bg-[#F5F6FA] text-[#6B7189]"}`}>{s}</span>;

export default function Purchasing({ warehouses, isOwner }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState(null);
  const [create, setCreate] = useState(false);
  const [createInitial, setCreateInitial] = useState(null);
  const load = useCallback(() => api.get("/vendor-pos").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const pid = params.get("reorder");
    if (!pid) return;
    api.get(`/inventory/product/${pid}/reorder-info`).then(({ data }) => {
      setCreateInitial({ supplier_id: data.supplier_id, warehouse_id: data.warehouse_id,
        lines: [{ product_id: data.product.id, code: data.product.code, name: data.product.name, unit: data.product.unit, quantity: data.suggested_qty, unit_cost: data.unit_cost }] });
      setCreate(true);
      if (!data.supplier_id) toast.message("Add a supplier to complete this reorder");
    }).catch((e) => toast.error(apiError(e)));
    const np = new URLSearchParams(params); np.delete("reorder"); setParams(np, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const closeCreate = () => { setCreate(false); setCreateInitial(null); };
  if (!rows) return <Loading />;
  return (
    <div data-testid="purchasing-tab">
      {isOwner && <div className="mb-3"><Button onClick={() => { setCreateInitial(null); setCreate(true); }} className="h-10 bg-[#0B7C5A] hover:bg-[#09664a]" data-testid="new-vpo-btn"><Plus className="w-4 h-4" /> New purchase order</Button></div>}
      {rows.length === 0 ? <Empty title="No purchase orders" hint="Raise a PO to a supplier; receiving it adds stock to a warehouse bin." /> : (
        <>
          <Card className="hidden sm:block overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F6FA] text-[#6B7189]"><tr className="text-left">
                <th className="px-4 py-3 font-medium">PO #</th>
                <th className="px-4 py-3 font-medium">Supplier</th>
                <th className="px-4 py-3 font-medium">Warehouse</th>
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium text-center">Status</th>
                <th className="px-4 py-3 font-medium text-right">Total</th>
                <th className="px-4 py-3"></th>
              </tr></thead>
              <tbody>{rows.map((po) => (
                <tr key={po.id} className="border-t border-[#E6E8F0] hover:bg-[#F5F6FA] cursor-pointer" onClick={() => navigate(`/inventory/purchase-orders/${po.id}`)} data-testid={`vpo-row-${po.id}`}>
                  <td className="px-4 py-3 font-medium text-[#1D4ED8]">{po.number}</td>
                  <td className="px-4 py-3 text-[#101D51]">{po.supplier?.name || "—"}</td>
                  <td className="px-4 py-3 text-[#6B7189]">{po.warehouse?.name || "—"}</td>
                  <td className="px-4 py-3 text-[#6B7189]">{fdate(po.date)}</td>
                  <td className="px-4 py-3 text-center"><Badge s={po.status} /></td>
                  <td className="px-4 py-3 text-right font-semibold text-[#101D51]">{formatINR(po.grand_total)}</td>
                  <td className="px-4 py-3 text-right"><ChevronRight className="w-4 h-4 text-[#6B7189]" /></td>
                </tr>))}</tbody>
            </table>
          </Card>
          <div className="sm:hidden space-y-2">{rows.map((po) => (
            <Card key={po.id} className="p-3 flex items-center gap-3 cursor-pointer hover:border-[#C7CBDA]" onClick={() => navigate(`/inventory/purchase-orders/${po.id}`)} data-testid={`vpo-card-${po.id}`}>
              <div className="h-9 w-9 rounded-lg bg-[#F5F6FA] text-[#101D51] flex items-center justify-center shrink-0"><Truck className="w-4 h-4" /></div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-[#101D51] truncate">{po.number} · {po.supplier?.name}</div>
                <div className="text-[11px] text-[#6B7189] truncate">{fdate(po.date)}</div>
              </div>
              <div className="text-right shrink-0"><div className="text-sm font-semibold text-[#101D51]">{formatINR(po.grand_total)}</div><Badge s={po.status} /></div>
            </Card>))}</div>
        </>
      )}
      {create && <CreateDialog warehouses={warehouses} initial={createInitial} onClose={closeCreate} onCreated={() => { closeCreate(); load(); }} />}
    </div>
  );
}

function ProductPicker({ onPick }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  useEffect(() => { const t = setTimeout(() => { if (q.trim()) api.get(`/products?q=${encodeURIComponent(q)}&page_size=8`).then((r) => setRes(r.data.items)); else setRes([]); }, 250); return () => clearTimeout(t); }, [q]);
  return (
    <div className="relative">
      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7189]" />
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search product to add…" className="pl-9 h-10 bg-white" data-testid="vpo-product-search" />
      {res.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-[#E6E8F0] rounded-lg shadow-lg max-h-56 overflow-y-auto">
          {res.map((p) => (
            <button key={p.id} onClick={() => { onPick(p); setQ(""); setRes([]); }} className="w-full text-left px-3 py-2 hover:bg-[#F5F6FA] text-sm" data-testid={`vpo-pick-${p.code}`}>
              <div className="font-medium text-[#101D51]">{p.name}</div><div className="text-[11px] text-[#6B7189]">{p.code}{p.brand ? ` · ${p.brand}` : ""}</div>
            </button>))}
        </div>)}
    </div>
  );
}

function CreateDialog({ warehouses, initial, onClose, onCreated }) {
  const [suppliers, setSuppliers] = useState([]);
  const [f, setF] = useState({ supplier_id: initial?.supplier_id || "", warehouse_id: initial?.warehouse_id || warehouses.find((w) => w.is_default)?.id || warehouses[0]?.id || "", expected_date: "", notes: "" });
  const [lines, setLines] = useState(initial?.lines || []);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/suppliers").then((r) => setSuppliers(r.data)); }, []);
  const addLine = (p) => { if (lines.find((l) => l.product_id === p.id)) return; setLines([...lines, { product_id: p.id, code: p.code, name: p.name, unit: p.unit, quantity: 1, unit_cost: (p.purchase_price || 0) / 100 }]); };
  const upd = (i, k, v) => setLines(lines.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const total = lines.reduce((s, l) => s + Math.round((Number(l.unit_cost) || 0) * 100) * (Number(l.quantity) || 0), 0);
  const save = async () => {
    if (!f.supplier_id) return toast.error("Choose a supplier");
    if (!f.warehouse_id) return toast.error("Choose a warehouse");
    if (lines.length === 0) return toast.error("Add at least one product");
    setSaving(true);
    try {
      await api.post("/vendor-pos", { ...f, expected_date: f.expected_date || null, lines: lines.map((l) => ({ product_id: l.product_id, quantity: Number(l.quantity), unit_cost: Number(l.unit_cost) })) });
      toast.success("Purchase order created"); onCreated();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl" data-testid="vpo-create-dialog">
        <DialogHeader><DialogTitle>New purchase order</DialogTitle><DialogDescription>Raise a PO to a supplier for a warehouse.</DialogDescription></DialogHeader>
        <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Supplier</Label>
              <Select value={f.supplier_id} onValueChange={(v) => setF({ ...f, supplier_id: v })}>
                <SelectTrigger className="mt-1 bg-white" data-testid="vpo-supplier"><SelectValue placeholder={suppliers.length ? "Select supplier" : "Add a supplier first"} /></SelectTrigger>
                <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label>Deliver to warehouse</Label>
              <Select value={f.warehouse_id} onValueChange={(v) => setF({ ...f, warehouse_id: v })}>
                <SelectTrigger className="mt-1 bg-white" data-testid="vpo-warehouse"><SelectValue /></SelectTrigger>
                <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label>Expected date</Label><Input type="date" className="mt-1 bg-white" value={f.expected_date} onChange={(e) => setF({ ...f, expected_date: e.target.value })} /></div>
            <div><Label>Notes</Label><Input className="mt-1 bg-white" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
          </div>
          <ProductPicker onPick={addLine} />
          {lines.length > 0 && (
            <div className="space-y-2">
              {lines.map((l, i) => (
                <div key={l.product_id} className="flex items-center gap-2 bg-[#F8F9FC] rounded-lg p-2" data-testid={`vpo-line-${l.code}`}>
                  <div className="min-w-0 flex-1"><div className="text-sm font-medium text-[#101D51] truncate">{l.name}</div><div className="text-[11px] text-[#6B7189]">{l.code}</div></div>
                  <div className="w-20"><Label className="text-[10px] text-[#6B7189]">Qty</Label><Input type="number" min="0" step="any" className="h-9 bg-white" value={l.quantity} onChange={(e) => upd(i, "quantity", e.target.value)} data-testid={`vpo-qty-${l.code}`} /></div>
                  <div className="w-28"><Label className="text-[10px] text-[#6B7189]">Unit cost ₹</Label><Input type="number" min="0" step="any" className="h-9 bg-white" value={l.unit_cost} onChange={(e) => upd(i, "unit_cost", e.target.value)} data-testid={`vpo-cost-${l.code}`} /></div>
                  <button onClick={() => setLines(lines.filter((_, idx) => idx !== i))} className="h-8 w-8 rounded-lg border border-[#E6E8F0] flex items-center justify-center shrink-0"><X className="w-4 h-4 text-[#6B7189]" /></button>
                </div>
              ))}
              <div className="text-right text-sm font-semibold text-[#101D51]">Total: {formatINR(total)}</div>
            </div>
          )}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={saving} className="bg-[#101D51]" data-testid="vpo-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create PO"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



